import { computed } from "mobx";

import { DateTime } from "@bps/date-time";
import { PatchUserAvailabilityDto } from "@libs/gateways/booking/BookingGateway.dtos.ts";
import { BookingStore } from "@stores/booking/BookingStore.ts";
import { User } from "@stores/core/models/User.ts";

import {
  AwayChoice,
  EditUserDayWorkingHoursData,
  EditUserDayWorkingHoursFormValues
} from "../components/EditUserDayWorkingHours.types.ts";
import { getReason } from "../utils.ts";

export class EditUserDayWorkingHoursModel {
  constructor(
    private booking: BookingStore,
    private user: User,
    private startDate: DateTime
  ) {}

  get core() {
    return this.booking.core;
  }

  @computed
  public get initialDate() {
    return this.startDate.isBeforeToday ? DateTime.today() : this.startDate;
  }

  @computed
  public get baseWorkingHoursQuery() {
    return {
      from: this.initialDate,
      to: this.initialDate.plus({ days: 1 }),
      userIds: [this.user.id]
    };
  }

  public fetch = async (): Promise<EditUserDayWorkingHoursData> => {
    const workingHoursPromise = this.booking.getUsersWorkingHours({
      ...this.baseWorkingHoursQuery,
      isStandardHours: false
    });

    const availabilityPromise = this.booking.getUserAvailability(this.user.id);

    const orgUnitHoursPromise = this.booking.getOrgUnitWorkingHours({
      from: this.startDate,
      to: this.startDate.plus({ days: 1 }),
      orgUnitId: this.core.locationId
    });

    const [workingHours, availability, orgUnitHours] = await Promise.all([
      workingHoursPromise,
      availabilityPromise,
      orgUnitHoursPromise
    ]);

    return {
      user: this.user,
      workingHours: workingHours[0]?.orgUnitRanges,
      availability,
      orgUnitHours
    };
  };

  fetchStandardHours = async (date: Date) => {
    const dateTime = DateTime.fromJSDate(date);

    const data = await this.booking.getUsersWorkingHours({
      ...this.baseWorkingHoursQuery,
      from: dateTime,
      to: dateTime.plus({ days: 1 }),
      isStandardHours: true
    });

    const userTimeRange = data[0];

    if (userTimeRange) {
      const { startTime, endTime } =
        userTimeRange.orgUnitRanges.startEndTime(dateTime);

      return { stdWorkingHoursStart: startTime, stdWorkingHoursEnd: endTime };
    }

    return { stdWorkingHoursStart: undefined, stdWorkingHoursEnd: undefined };
  };

  public getInitialValues = (
    data: EditUserDayWorkingHoursData
  ): EditUserDayWorkingHoursFormValues => {
    const workingHours = data.workingHours;

    const reason = getReason(data.availability, this.initialDate);
    const date = this.initialDate.toJSDate();

    if (workingHours) {
      const startEndResult = workingHours?.startEndTime(this.initialDate);

      const { startTime: _startTime, endTime: _endTime } = startEndResult;

      const isAway = !_startTime && !_endTime;

      const startTime = _startTime?.toTimeInputFormat();

      const endTime = _endTime?.toTimeInputFormat();

      const activeTimeRange = data.workingHours?.timeRanges?.[0];

      return {
        date,
        awayChoice: isAway ? AwayChoice.away : AwayChoice.present,
        startTime,
        endTime,
        reason,
        orgUnitId: activeTimeRange?.orgUnitId
      };
    }

    return {
      date,
      awayChoice: AwayChoice.present,
      startTime: undefined,
      endTime: undefined,
      reason,
      orgUnitId: data.availability.availableOrgUnitIds?.[0]
    };
  };

  public handleSubmitForm = async (
    values: EditUserDayWorkingHoursFormValues
  ) => {
    if (!this.user || !values.date) {
      return;
    }

    const userAvailability = await this.booking.getUserAvailability(
      this.user.id
    );

    const isAvailable = values.awayChoice === "present";

    const date = DateTime.fromJSDate(values.date);

    // try to match an existing override before attempting to append a new one
    const existingOverride = userAvailability.dto.bookingScheduleOverrides.find(
      x =>
        date.equals(DateTime.fromISO(x.startDate)) &&
        date.equals(DateTime.fromISO(x.endDate))
    );

    const bookingScheduleOverrides: PatchUserAvailabilityDto["bookingScheduleOverrides"] =
      [
        ...userAvailability.scheduleOverrides.filter(
          x => !existingOverride || x.id !== existingOverride.id
        ),
        {
          ...existingOverride,
          orgUnitId: values.orgUnitId ?? this.core.locationId,
          startDate: date.toISODate(),
          endDate: date.toISODate(),
          startTime: values.startTime,
          endTime: values.endTime,
          isAvailable,
          reason: values.reason
        }
      ];

    await this.booking.updateUserAvailability({
      userId: this.user.id,
      bookingScheduleOverrides
    });
  };
}
